import React, {
    useEffect,
    useState,
    useMemo,
    MutableRefObject,
    useCallback,
    Dispatch,
    SetStateAction,
} from 'react';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {mapBreadcrumb} from 'hooks/Product/helpers/mapBreadcrumb';
import {useHardwareItem} from 'hooks/Product/Product';
import {RECENTLY_ADDED_KEYS} from 'hooks/RecentlyAdded';
import {Alert, Button, Col, Form, Image, Row} from 'react-bootstrap';
import {parseHtmlString} from 'shared/helpers/HTMLParser';
import {
    CBCButton,
    Icon,
    Loader,
    useComponentMountedHelper,
    usePromise,
} from 'shared/helpers';
import {useProductContext} from 'contexts/ProductContext';
import {useJobContext} from 'contexts/JobContext';
import {Dropdown} from 'shared/components/Forms/Dropdown';
import {ImageSlider} from 'shared/components/ImageSlider/ImageSlider';
import {Items} from 'shared/components/Product/Items';
import {useConfirmationDialog} from 'shared/components/ConfirmationDialog/ConfirmationDialog';
import {getBreadCrumb} from 'service/HardwareService';
import {size, pickBy} from 'lodash';
import {invalidateJobCost} from 'components/customer/Job/store/jobApi';
import {useAppDispatch} from 'store/customer';
import {CancelButton} from 'shared/components/CancelButton';
import styled from 'styled-components';
import {PartialJob} from 'shared/types/PartialJob';
import {BreadCrumbApi} from 'hooks/Product/useBreadCrumb';
import {ItemType} from 'shared/components/Product/Item';

interface HardwareItemProps {
    scrollToTop?: MutableRefObject<HTMLElement>;
}

interface Attribute {
    id: string;
    name: string;
    freeText: string;
}

interface ItemVariation {
    description: string;
    imageUrl: string;
    code: string;
    salePrice: string;
    regularPrice: number;
    stockStatus: string;
    stockStatusText: string;
}

interface ItemDetails extends ItemVariation {
    name: string;
    favourites: string;
    item_attributes: Attribute[];
    id: number;
    advanced: boolean;
    linked_products: {
        [key: number]: ItemType;
    };
    item_images: {
        isPrimary: number;
        imageUrl: string;
    }[];
}

export const HardwareItem = ({scrollToTop}: HardwareItemProps) => {
    const [searchParams] = useSearchParams();

    const sundry = searchParams.get('sundry') || null;
    const category = searchParams.get('category') || null;
    const jobSundryId = searchParams.get('jobSundry') || null;

    const navigate = useNavigate();
    const {
        AddToCart: addToCartOG,
        AddToFavourite: addToFavouriteOG,
        addRecentItem,
        resetBreadCrumb,
    } = useProductContext();
    const {job} = useJobContext() as PartialJob;
    const {dialog} = useConfirmationDialog();
    const {
        loading,
        details,
        advancedAttributes,
        advancedAttributeValues,
        attributeIdStore,
        setAttributeValue,
        itemVariation,
        dropdownLoading,
        selectedAdvancedAttributes,
        count,
        setCount,
    }: {
        loading: boolean;
        details: Partial<ItemDetails>;
        advancedAttributes: Partial<{[key: string]: {value: string}[]}>;
        advancedAttributeValues: Partial<{[key: string]: string}>;
        attributeIdStore: React.MutableRefObject<object>;
        setAttributeValue: (key: string, value: string) => void;
        itemVariation: Partial<ItemVariation>;
        dropdownLoading: boolean;
        selectedAdvancedAttributes: {[key: string]: string};
        count: number;
        setCount: Dispatch<SetStateAction<number>>;
    } = useHardwareItem(sundry, jobSundryId);
    const {isMounted} = useComponentMountedHelper();
    const {jobId} = useParams();

    const [isFavourite, setIsFavourite] = useState(false);
    const [favouriteButtonDisabled, setFavouriteButtonDisabled] =
        useState(false);
    const [cartButtonDisabled, setCartButtonDisabled] = useState(false);
    const [errorMessages, setErrorMessages] = useState<string[]>([]);

    const executeScroll = () => scrollToTop.current.scrollIntoView();
    const dispatch = useAppDispatch();

    // cutomer price adjust
    const customerPriceAdjust = 1 * (job && 1 + job.customerPriceAdjust / 100);
    const supplierPriceAdjust = 1 * (job && 1 + job.supplierPriceAdjust / 100);

    const buttonLabel = jobSundryId ? 'Save' : 'Add To Cart';
    const iconName = jobSundryId ? 'Button-Tick.svg' : 'Button-Add.svg';
    useEffect(() => {
        return usePromise(
            ([breadCrumbs]: [BreadCrumbApi[]]) => {
                resetBreadCrumb(mapBreadcrumb(breadCrumbs, Number(jobId)));
            },
            [getBreadCrumb(category)]
        );
    }, [category]);

    useEffect(() => {
        setIsFavourite(parseInt(details.favourites) > 0);
    }, [details]);

    const increment = useCallback(() => {
        setCount((count) => count + 1);
    }, []);

    const decrement = useCallback(() => {
        setCount((count) => (count > 1 ? count - 1 : count));
    }, []);

    const addToFavourite = useCallback(async () => {
        setFavouriteButtonDisabled(true);
        await addToFavouriteOG(details, !isFavourite);

        setIsFavourite(!isFavourite);
        setFavouriteButtonDisabled(false);
    }, [setFavouriteButtonDisabled, addToFavouriteOG, details, isFavourite]);

    const addToFavouriteHandler = useCallback(() => {
        void addToFavourite();
    }, [addToFavourite]);

    const addToCart = useCallback(async () => {
        if (!count || count < 1) {
            // show error message
            setErrorMessages(['Quantity must be greater than 0']);
            if (scrollToTop) {
                executeScroll();
            }
            return;
        }
        // check all advanced attributes fields filled
        const extraAttributesCount: number =
            (details && details.item_attributes.length) || 0;
        // find all the empty values in fields
        const emptyAttrFieldValues = pickBy(
            advancedAttributeValues,
            (obj) => obj == ''
        );

        if (
            extraAttributesCount != size(advancedAttributeValues) ||
            size(emptyAttrFieldValues) > 0
        ) {
            // show error message

            setErrorMessages([
                'You must fill out all attributes before you can continue.',
            ]);
            if (scrollToTop) {
                executeScroll();
            }
            return;
        }
        if (itemVariation) {
            setErrorMessages([]);
            setCartButtonDisabled(true);

            await addToCartOG(
                details,
                count,
                advancedAttributeValues,
                attributeIdStore.current as {[key: string]: number | string},
                itemVariation,
                jobSundryId
            );
            addRecentItem(details.id, RECENTLY_ADDED_KEYS.HARDWARE);
            dispatch(invalidateJobCost());
            isMounted.current && setCartButtonDisabled(false);
            void navigate(`/v2/job/${jobId}/dashboard`);
        } else {
            // show error message
            setErrorMessages(['Please select valid variation']);
        }
    }, [
        count,
        advancedAttributeValues,
        executeScroll,
        addToCartOG,
        addRecentItem,
        dispatch,
        jobId,
        jobSundryId,
        details,
        itemVariation,
    ]);

    const addToCartHandler = useCallback(() => {
        void addToCart();
    }, [addToCart]);

    const keyUpHandler = useCallback(
        (attribute: Attribute) =>
            (event: React.KeyboardEvent<HTMLInputElement>) => {
                setAttributeValue(attribute.id, event.currentTarget.value);
            },
        [setAttributeValue]
    );

    const onBlurHandler = useCallback(
        (event: React.FocusEvent<HTMLInputElement>) => {
            if (event.currentTarget.value == '') {
                setErrorMessages(["Quantity can't be empty"]);
                if (scrollToTop) {
                    executeScroll();
                }
            }
        },
        []
    );

    const onChangeHandler = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setCount(parseInt(event.currentTarget.value));
        },
        []
    );

    useEffect(() => {
        if (count < 1) {
            setErrorMessages(['Quantity must be greater than 0']);
            if (scrollToTop) {
                executeScroll();
            }
            return;
        }
    }, [count]);

    useEffect(() => {
        setCartButtonDisabled(false);
        if (details.advanced) {
            if (
                itemVariation &&
                Object.keys(itemVariation).length &&
                Number(itemVariation.stockStatus) == 1
            ) {
                setCartButtonDisabled(true);
            }
        } else {
            if (Number(details.stockStatus) == 1) {
                // sundry out of stock. Disable cart button
                setCartButtonDisabled(true);
            }
        }
    }, [itemVariation, details]);

    const rawDescription = useMemo(() => {
        if (itemVariation && itemVariation.description) {
            return itemVariation.description;
        }

        return details.description;
    }, [itemVariation, details]);

    const description = useMemo(() => {
        if (typeof rawDescription == 'string') {
            return parseHtmlString(rawDescription);
        }

        return '';
    }, [rawDescription]);

    const linkedProducts = useMemo(() => {
        if (!details || !details.linked_products) {
            return [];
        }

        return Object.values(details.linked_products);
    }, [details]);

    return (
        <Loader hideInitially={true} loader={loading}>
            {dialog}
            {errorMessages.length ? (
                <div className="jobFormErrors" style={{marginBottom: '15px'}}>
                    {errorMessages.map((errorMessage) => {
                        return (
                            <Alert variant="danger" key={errorMessage}>
                                <Icon iconName="Button-Error.svg" />
                                <div>{errorMessage}</div>
                            </Alert>
                        );
                    })}
                </div>
            ) : null}

            <Row className="hardware-product-details">
                <Col md={{offset: 1, span: 5}} className="product-images">
                    <Button
                        onClick={addToFavouriteHandler}
                        disabled={favouriteButtonDisabled}
                        variant="btn btn-link"
                        className={
                            isFavourite
                                ? 'itemFavourite userFavourite'
                                : 'itemFavourite'
                        }
                    />

                    {details.item_images &&
                    Object.values(details.item_images).length ? (
                        <ImageSlider
                            images={Object.values(details.item_images)
                                .sort((a, b) => a.isPrimary - b.isPrimary)
                                .map((image) => `/${image.imageUrl}`)}
                            variationImage={
                                itemVariation && itemVariation.imageUrl
                                    ? `/${itemVariation.imageUrl}`
                                    : ''
                            }
                        />
                    ) : (
                        <div className="imageContainer">
                            {details.hasOwnProperty('imageUrl') ? (
                                <Image
                                    src={`/${
                                        itemVariation && itemVariation.imageUrl
                                            ? itemVariation.imageUrl
                                            : details.imageUrl
                                    }`}
                                    alt={details.name}
                                />
                            ) : null}
                        </div>
                    )}
                </Col>

                <Col md={5} className="product-details">
                    <h1>
                        {details.name}&nbsp;
                        <span>
                            (
                            {details.advanced
                                ? itemVariation &&
                                  Object.keys(itemVariation).length
                                    ? itemVariation.code
                                    : '-'
                                : details.code}
                            )
                        </span>
                    </h1>

                    {details.advanced ? (
                        itemVariation && Object.keys(itemVariation).length ? (
                            <div className="price">
                                {parseFloat(itemVariation.salePrice) ? (
                                    <>
                                        <strong>
                                            ${' '}
                                            {customerPriceAdjust
                                                ? (
                                                      Number(
                                                          itemVariation.salePrice
                                                      ) *
                                                      customerPriceAdjust *
                                                      supplierPriceAdjust
                                                  ).toFixed(2)
                                                : itemVariation.salePrice}
                                        </strong>
                                        <span> +{job.countryTaxRateName}</span>
                                        <p>
                                            was:{' '}
                                            <span
                                                style={{
                                                    color: 'red',
                                                    textDecoration:
                                                        'line-through',
                                                }}>
                                                $
                                                {customerPriceAdjust
                                                    ? (
                                                          itemVariation.regularPrice *
                                                          customerPriceAdjust *
                                                          supplierPriceAdjust
                                                      ).toFixed(2)
                                                    : itemVariation.regularPrice}
                                            </span>
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <strong>
                                            ${' '}
                                            {customerPriceAdjust
                                                ? (
                                                      itemVariation.regularPrice *
                                                      customerPriceAdjust *
                                                      supplierPriceAdjust
                                                  ).toFixed(2)
                                                : itemVariation.regularPrice}
                                        </strong>
                                        <span> +{job.countryTaxRateName}</span>
                                    </>
                                )}
                            </div>
                        ) : (
                            <div className="price">-</div>
                        )
                    ) : (
                        <div className="price">
                            {parseFloat(details.salePrice) ? (
                                <>
                                    <strong>
                                        ${' '}
                                        {customerPriceAdjust
                                            ? (
                                                  Number(details.salePrice) *
                                                  customerPriceAdjust *
                                                  supplierPriceAdjust
                                              ).toFixed(2)
                                            : details.salePrice}
                                    </strong>
                                    <span> +{job.countryTaxRateName}</span>
                                    <p>
                                        was:{' '}
                                        <span
                                            style={{
                                                color: 'red',
                                                textDecoration: 'line-through',
                                            }}>
                                            $
                                            {customerPriceAdjust
                                                ? (
                                                      details.regularPrice *
                                                      customerPriceAdjust *
                                                      supplierPriceAdjust
                                                  ).toFixed(2)
                                                : details.regularPrice}
                                        </span>
                                    </p>
                                </>
                            ) : (
                                <>
                                    <strong>
                                        ${' '}
                                        {customerPriceAdjust
                                            ? (
                                                  details.regularPrice *
                                                  customerPriceAdjust *
                                                  supplierPriceAdjust
                                              ).toFixed(2)
                                            : details.regularPrice}
                                    </strong>
                                    <span> +{job.countryTaxRateName}</span>
                                </>
                            )}
                        </div>
                    )}

                    {/* add stock status */}
                    {details.advanced ? (
                        // advanced sundry
                        itemVariation && Object.keys(itemVariation).length ? (
                            parseInt(itemVariation.stockStatus) == 1 ? (
                                <span style={{color: 'red'}}>
                                    {itemVariation.stockStatusText}
                                </span>
                            ) : (
                                <span>{itemVariation.stockStatusText}</span>
                            )
                        ) : null
                    ) : // simple sundry
                    parseInt(details.stockStatus) == 1 ? (
                        <span style={{color: 'red'}}>
                            {details.stockStatusText}
                        </span>
                    ) : (
                        <span>{details.stockStatusText}</span>
                    )}
                    <h2>Specifications:</h2>
                    <div className="details">{description}</div>
                    <div className="quantity cbc-form" style={{padding: '0'}}>
                        <Form.Group as={Row} controlId={`quantity_`}>
                            <Form.Label column md={4} sm={5} xs={3}>
                                <strong>Quantity</strong> : <i>*</i>
                            </Form.Label>
                            <Col md={4} className="numberSpinner">
                                <Form.Control
                                    as="input"
                                    type="number"
                                    value={count}
                                    onBlur={onBlurHandler}
                                    onChange={onChangeHandler}
                                />
                                <div className="spinner">
                                    <div onClick={increment}>&and;</div>
                                    <div onClick={decrement}>&or;</div>
                                </div>
                            </Col>
                        </Form.Group>

                        {Object.keys(advancedAttributes).length
                            ? details.item_attributes &&
                              details.item_attributes.map((attribute) => {
                                  return (
                                      <Form.Group
                                          as={Row}
                                          controlId={`${attribute.id}_`}
                                          key={attribute.name}>
                                          <Form.Label
                                              column
                                              md={4}
                                              sm={6}
                                              xs={3}>
                                              <strong>
                                                  {attribute.name} :
                                              </strong>
                                          </Form.Label>
                                          <Col md={6}>
                                              {attribute.freeText ? (
                                                  <Form.Control
                                                      type="text"
                                                      name={attribute.id}
                                                      defaultValue={
                                                          selectedAdvancedAttributes[
                                                              attribute.name
                                                          ]
                                                      }
                                                      onKeyUp={keyUpHandler(
                                                          attribute
                                                      )}
                                                  />
                                              ) : (
                                                  <Dropdown
                                                      options={
                                                          advancedAttributes[
                                                              attribute.id
                                                          ]
                                                      }
                                                      name={attribute.id}
                                                      loader={dropdownLoading}
                                                      value={
                                                          advancedAttributeValues[
                                                              attribute.id
                                                          ]
                                                      }
                                                      selectHandler={
                                                          setAttributeValue
                                                      }
                                                  />
                                              )}
                                          </Col>
                                      </Form.Group>
                                  );
                              })
                            : null}
                    </div>
                </Col>
            </Row>
            <Row className="hardware-item-buttons">
                <Col
                    md={{offset: 3, span: 3, order: 'first'}}
                    xs={{span: 12, order: 'last'}}>
                    <ButtonContainer>
                        <CancelButton noDialog />
                    </ButtonContainer>
                </Col>
                <Col
                    md={{span: 3, order: 'last'}}
                    xs={{span: 12, order: 'first'}}>
                    <CBCButton
                        disabled={cartButtonDisabled}
                        onClick={addToCartHandler}
                        type="submit"
                        iconName={iconName}
                        className="item-button button-blue">
                        {buttonLabel}
                    </CBCButton>
                </Col>
            </Row>
            {linkedProducts.length > 0 ? (
                <Items items={linkedProducts} title="Related Products" />
            ) : null}
        </Loader>
    );
};

const ButtonContainer = styled.div`
    width: 100%;

    > button {
        width: 100%;
    }
`;
